import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import VideoInputDeviceList from '../VideoInputDeviceList'
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import classnames from 'classnames';
import isStringSet from '../../utilities/is/isStringSet';
import appStore from '../../utilities/store';
import DialogTransition from '../DialogTransition';
import styles from './styles';
import { CenteredMessage } from '../Typography';
import { processQRCode, QRType } from '../../utilities/qr';
import { withStyles } from '@material-ui/core/styles';
import { BrowserQRCodeReader } from '@zxing/library';

class QRCodeReaderDialog extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            mirror: !!appStore.get('store:video/mirror'),
            deviceId: appStore.get('store:video/deviceId'),
            deviceList: []
        };
        this.codeReader = new BrowserQRCodeReader();
        this.isScanning = false;
        this.videoRef = React.createRef();
    }

    onDeviceListLoaded = (deviceList) => {
        this.setState({ deviceList });
    };

    componentWillUnmount() {
        this.stopCapture();
    }

    onDeviceChanged = (deviceId) => {
        const isScanning = this.isScanning;

        appStore.set('store:video/deviceId', deviceId);

        if (isScanning) {
            this.codeReader.reset();
        }
        this.isScanning = false;
        this.startCapture(deviceId);
    };

    handleClose = () => {        
        this.stopCapture();
        this.props.handleClose();
    };

    startCapture = (deviceId = this.state.deviceId) => {

        if (!document.querySelector("#video-preview")) {
            return;
        }

        this.isScanning = true;
        this.forceUpdate();

        deviceId = deviceId === '' ? undefined : deviceId;

        this.codeReader.decodeFromInputVideoDevice(deviceId, 'video-preview')
            .then((result) => {
                processQRCode(result.text || "").then(qrData => {
                    if (qrData.qrType !== QRType.Unknown) {
                        this.stopCapture();

                        if (qrData.qrType === QRType.Card) {
                            this.props.onCardScanned(qrData.qrCode, () => {
                                this.restartCapture();
                            });
                        } else if (qrData.qrType === QRType.VisitorPass) {
                            this.props.onVisitorPassScanned(qrData.qrCode, () => {
                                this.restartCapture();
                            });
                        }
                    } else {
                        this.props.onScanError("Unidentified QR code");
                    }
                }).catch(err => console.error(err));

            }).catch((err) => {
                if (err.name === "NotReadableError") {
                    this.props.onScanError(
                        "Another window is using this device, please check and try again");
                }
            });
    };

    stopCapture = () => {
        this.codeReader.reset();
    };

    handleMirrorChanged = (event) => {
        appStore.set('store:video/mirror', event.target.checked);
        this.setState({ mirror: event.target.checked });
    };

    restartCapture = () => {
        this.startCapture();
    };

    render() {
        const { classes } = this.props;
        const { mirror, deviceId, deviceList } = this.state;
        const { isScanning } = this;

        const scanning = isScanning;
        const isDeviceSelected = isStringSet(deviceId);
        const isOpen = Boolean(this.props.open);
        const hasDevice = deviceList !== null && deviceList.length > 0;

        return (
            <div>
                {isOpen &&
                    <Dialog
                        fullScreen
                        onClose={this.handleClose}
                        className={classes.dialog}
                        open={isOpen}
                        TransitionComponent={DialogTransition}
                        aria-labelledby="qrcode-dialog-title"
                        aria-describedby="qrcode-dialog-description">
                        <AppBar className={classes.appBar} color="primary">
                            <Toolbar>
                                <IconButton color="inherit" onClick={this.handleClose} aria-label="Close">
                                    <CloseIcon />
                                </IconButton>
                                <Typography variant="h6" color="inherit" className={classes.flex} noWrap>
                                    {this.props.title}
                                </Typography>
                            </Toolbar>
                        </AppBar>

                        <Grid container spacing={2} direction="row" alignItems="center" justifyContent="center">
                            <Grid item xs={12}>
                                {!isDeviceSelected && !hasDevice &&
                                    <CenteredMessage className={classes.scanMessage}>
                                        No webcam detected
                                    </CenteredMessage>
                                }
                                <video
                                    id="video-preview"
                                    ref={this.videoRef}
                                    width="100%"
                                    height="100%"
                                    className={classnames(classes.videoPreview, mirror ? classes.flip : '')}>
                                </video>
                                {scanning && isDeviceSelected && hasDevice &&
                                    <React.Fragment>
                                        <div className={classes.targetOverlayTop}></div>
                                        <div className={classes.targetOverlayLeft}></div>
                                        <div className={classes.targetOverlayRight}></div>
                                        <div className={classes.targetOverlayBottom}></div>
                                        <div className={classes.targetOverlay}></div>
                                    </React.Fragment>
                                }
                            </Grid>
                        </Grid>

                        <AppBar className={classes.bottomBar} color="default">
                            <Grid container alignItems="center">
                                <Grid item xs={12} sm={8} >
                                    <div className={classes.devicePicker}>
                                        <VideoInputDeviceList
                                            codeReader={this.codeReader}
                                            onDeviceChanged={this.onDeviceChanged}
                                            deviceId={deviceId}
                                            isScanning={this.isScanning}
                                            startCapture={this.startCapture}
                                            stopCapture={this.stopCapture}
                                            onDeviceListLoaded={this.onDeviceListLoaded}>
                                        </VideoInputDeviceList>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        className={classes.mirror}
                                        control={
                                            <Switch
                                                checked={!!mirror}
                                                onChange={this.handleMirrorChanged}
                                                value="flip"
                                                color="secondary"
                                            />
                                        }
                                        label="Mirror">
                                    </FormControlLabel>
                                </Grid>
                            </Grid>
                        </AppBar>
                    </Dialog>
                }
            </div >
        );
    }
}

QRCodeReaderDialog.propTypes = {
    onCardScanned: PropTypes.func.isRequired,
    onVisitorPassScanned: PropTypes.func.isRequired,
    handleClose: PropTypes.func.isRequired,
    onScanError: PropTypes.func,
    title: PropTypes.string.isRequired,
    open: PropTypes.bool,
};

export default withStyles(styles)(QRCodeReaderDialog);
